import { setDirtyForm } from 'app/actions/dirty-forms.actions';
import { checkAccess } from 'app/actions/app.actions';
import { fetchBanners } from 'app/actions/banners.actions';
import * as api from 'app/resources/banners.api';
import appHelpers from 'app/app-helpers';
import _ from 'lodash';
import template from './banners-management.html';

import previewBannerDialogTemplate from './components/preview-banner-dialog/preview-banner-dialog.html';
import uploadBannerDialogTemplate from './components/upload-banner-dialog/upload-banner-dialog.html';
import UploadBannerController from './components/upload-banner-dialog/upload-banner-dialog-controller';

const SECOND_HAND_PERMISSION = 'cms.bannersManagement.approve';
const DIRTY_FORM = 'bannersManagement';

const mapStateToThis = ({ session, config, ui, banners, dirtyForms }) => {
  const { locale } = session;
  const { locales, maxBannersNumber, secondHandApproval, urlPattern, urlPatternError, jurisdictionId } = config;
  const { selectedLanguage } = ui;
  const { items, interval, draft } = banners;

  return {
    locale,
    locales,
    maxBannersNumber,
    secondHandApproval,
    urlPattern,
    urlPatternError,
    jurisdictionId,
    selectedLanguage,
    items,
    interval,
    draft,
    dirtyForm: dirtyForms[DIRTY_FORM],
  };
};

const mapDispatchToThis = {
  fetchBanners,
  setDirtyForm,
  checkAccess,
};

// eslint-disable-next-line no-undef
@Inject('$ngRedux', 'gettextCatalog', 'Notifications')
class BannersManagementController {
  constructor() {
    this.unsubscribe = this.$ngRedux.connect(mapStateToThis, mapDispatchToThis)(this);
    this.DIRTY_FORM = DIRTY_FORM;
  }

  $onDestroy() {
    this.unsubscribe();
  }

  async $onInit() {
    this.asSortableOptions = {
      allowDuplicates: true,
      dragMove: appHelpers.dragMove,
      orderChanged: () => {
        this.setDirtyForm(this.DIRTY_FORM, true);
      },
    };
    this.secondHandApprovalActive = this.checkAccess(SECOND_HAND_PERMISSION);
    await this.initBanners();
  }

  cancel() {
    this.setDirtyForm(this.DIRTY_FORM, false);
    return this.copyItems();
  }

  async initBanners() {
    await this.fetchBanners();
    this.copyItems();
    this.setEntries(this.locale);
  }

  copyItems() {
    this.itemsCopy = _.cloneDeep(this.items);
    this.intervalCopy = this.interval;
  }

  setEntries(locale) {
    this.language = locale;
    const key = Object.keys(this.itemsCopy).find((localeKey) => localeKey === locale);
    if (!key) {
      this.itemsCopy[locale] = {};
    }
  }

  discardChanges() {
    return api
      .discardBanners(this.jurisdictionId)
      .then(() => this.Notifications.alertSuccess(this.gettextCatalog.getString('Changes have been discarded')))
      .then(() => this.initBanners());
  }

  publishChanges() {
    api
      .publishBanners(this.secondHandApproval, this.jurisdictionId, this.itemsCopy, this.intervalCopy)
      .then(() => this.Notifications.alertSuccess(this.getSuccessText()))
      .then(() => this.initBanners())
      .then(() => this.setDirtyForm(this.DIRTY_FORM, false));
  }

  getSuccessText() {
    return this.secondHandApprovalActive || !this.secondHandApproval
      ? this.gettextCatalog.getString('Changes have been published')
      : this.gettextCatalog.getString('Changes have been submitted for approval');
  }

  previewBanners() {
    this.Notifications.showComplexDialog({
      template: previewBannerDialogTemplate,
      resolve: {
        items: () => this.itemsCopy,
        interval: () => this.intervalCopy,
        language: () => this.language,
      },
    });
  }

  removeBanner(index) {
    this.setDirtyForm(this.DIRTY_FORM, true);
    this.itemsCopy[this.language].splice(index, 1);
  }

  editBanner(banner, index) {
    this.Notifications.showComplexDialog({
      size: 'md',
      template: uploadBannerDialogTemplate,
      bindToController: true,
      controller: UploadBannerController,
      resolve: {
        locales: () => this.locales,
        language: () => this.language,
        publishBannerData: () => _.cloneDeep(banner),
        dialogTitle: () => this.gettextCatalog.getString('Edit Banner'),
        saveButtonText: () => this.gettextCatalog.getString('Save'),
        urlPattern: () => this.urlPattern,
        urlPatternError: () => this.urlPatternError,
      },
    }).result.then((editedBanner) => {
      if (!_.isEqual(banner, editedBanner)) this.setDirtyForm(this.DIRTY_FORM, true);
      this.itemsCopy[this.language][index] = editedBanner;
    });
  }

  uploadBanner() {
    this.Notifications.showComplexDialog({
      size: 'md',
      template: uploadBannerDialogTemplate,
      bindToController: true,
      controller: UploadBannerController,
      resolve: {
        locales: () => this.locales,
        language: () => this.language,
        dialogTitle: () => this.gettextCatalog.getString('Upload Banner'),
        saveButtonText: () => this.gettextCatalog.getString('Submit'),
        urlPattern: () => this.urlPattern,
        urlPatternError: () => this.urlPatternError,
      },
    }).result.then((uploadedBanner) => {
      this.setDirtyForm(this.DIRTY_FORM, true);
      this.itemsCopy[this.language] = this.itemsCopy[this.language].concat(uploadedBanner);
    });
  }

  onIntervalChange() {
    this.setDirtyForm(this.DIRTY_FORM, true);
  }

  isDiscardChangesAvailable() {
    return this.secondHandApproval && this.secondHandApprovalActive && this.draft;
  }

  isSubmitForApprovalAvailable() {
    return this.secondHandApproval && !this.secondHandApprovalActive && this.dirtyForm;
  }

  isPublishChangesAvailable() {
    return (
      (!this.secondHandApproval && this.dirtyForm) ||
      (this.secondHandApproval && this.secondHandApprovalActive && this.draft) ||
      (this.secondHandApproval && this.secondHandApprovalActive && this.dirtyForm)
    );
  }
}

export default class BannersManagementComponent {
  constructor() {
    this.controller = BannersManagementController;
    this.template = template;
    return this;
  }
}



// WEBPACK FOOTER //
// ./src/modules/app/components/home/management/components/banners/banners-management-component.js